import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import type { DiscussionMessage } from '@/lib/types';
import { getInitials, avatarClass } from '@/pages/home/activityMeta';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface DiscussionPanelProps {
  messages: DiscussionMessage[];
  onSend: (text: string) => void;
  onDelete: (commentId: string) => void;
  canModerate: boolean;
  mobileActions?: React.ReactNode;
}

const MAX_PREVIEW = 3;

export default function DiscussionPanel({ messages, onSend, onDelete, canModerate, mobileActions }: DiscussionPanelProps) {
  const [draft, setDraft] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate()

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    e.preventDefault();

    if (!user) {
      return;
    }

    const text = draft.trim();
    if (!text) return;

    onSend(text);
    setDraft('');
  };

  const togglePanel = () => {
    setIsOpen((prev) => !prev);
  };

  const visibleMessages = expanded
    ? messages
    : messages.slice(0, MAX_PREVIEW);

  const showExpand =
    messages.length > MAX_PREVIEW && !expanded;

  const count = messages.length;

  return (
    <section
      className="
        fixed bottom-16 inset-x-0 z-[10001]
        bg-white
        border-t border-background-200
        shadow-[0_-4px_16px_rgba(0,0,0,0.08)]

        md:static
        md:z-auto
        md:border
        md:rounded-lg
        md:shadow-none
        md:overflow-hidden
      "
    >
      {/* En-tête */}
      <button
        type="button"
        onClick={togglePanel}
        className="
          w-full flex items-center gap-2
          px-4 py-2
          text-left
          cursor-pointer

          md:cursor-default
          md:px-5
          md:pt-5
          md:pb-1
        "
      >
        <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
          <i className="ri-chat-3-line text-accent-600 text-sm"></i>
        </div>

        <h2 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
          Discussion
        </h2>

        {count > 0 && (
          <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold whitespace-nowrap">
            {count} message{count > 1 ? 's' : ''}
          </span>
        )}

        <i
          className={`
            ml-auto
            ri-arrow-up-s-line
            text-lg text-foreground-400
            transition-transform duration-300
            md:hidden
            ${isOpen ? '' : 'rotate-180'}
          `}
        ></i>
      </button>

      {/* Texte desktop uniquement */}
      <p className="hidden md:block px-5 pb-3 text-xs text-foreground-400">
        Pose une question ou coordonnez-vous directement ici.
      </p>

      {/* Messages */}
      <div
        className={`
          overflow-hidden
          transition-[max-height,opacity] duration-300

          ${isOpen
            ? 'max-h-[55vh] opacity-100'
            : 'max-h-0 opacity-0'
          }

          md:max-h-none
          md:opacity-100
          md:overflow-visible
        `}
      >
        <div
          className="
            max-h-[55vh] overflow-y-auto
            border-t border-background-200
            px-4 py-3
            flex flex-col gap-4

            md:max-h-none
            md:overflow-visible
            md:border-t-0
            md:px-5
            md:pt-2
            md:pb-0
          "
        >
          {messages.length === 0 && (
            <div className="text-center py-4">
              <div className="w-12 h-12 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-2">
                <i className="ri-chat-new-line text-accent-500 text-lg"></i>
              </div>

              <p className="text-sm text-foreground-500">
                Soyez le premier à écrire un message !
              </p>
            </div>
          )}

          {visibleMessages.map((m) => (
            <div key={m.id} className="group flex gap-3">
              <span
                className={`
                  w-9 h-9 rounded-full
                  ${avatarClass(m.author)}
                  flex items-center justify-center
                  text-xs font-bold shrink-0
                `}
              >
                {getInitials(m.author)}
              </span>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-foreground-900">
                    {m.author}
                  </span>

                  <span className="text-xs text-foreground-400">
                    {m.time}
                  </span>

                  {(m.isOwner || canModerate) && !m.id.startsWith("temp-") && (
                    <button
                      type="button"
                      onClick={() => onDelete(m.id)}
                      className="
                        ml-auto
                        opacity-0
                        group-hover:opacity-100
                        focus:opacity-100
                        text-xs font-medium
                        text-red-500
                        hover:text-red-700
                        transition-opacity
                        cursor-pointer
                      "

                      title="Supprimer ce commentaire"
                    >
                      <i className="ri-delete-bin-line"></i>
                      <span className="sr-only">Supprimer</span>
                    </button>
                  )}
                </div>

                <p className="mt-0.5 text-sm text-foreground-600 leading-relaxed">
                  {m.text}
                </p>
              </div>
            </div>
          ))}

          {showExpand && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="
                self-start
                text-sm font-semibold
                text-accent-600
                hover:text-accent-700
                transition-colors
                cursor-pointer
              "
            >
              Voir tous les messages
            </button>
          )}
        </div>
      </div>

      {/* Champ toujours visible */}
      <form
        onSubmit={handleSubmit}
        className="
          flex items-center gap-2
          border-t border-background-200
          bg-white
          px-4 pt-3
          pb-[calc(0.75rem+env(safe-area-inset-bottom))]

          md:mt-4
          md:px-5
          md:py-4
        "
      >
        <label htmlFor="discussion-message" className="sr-only">
          Message
        </label>

        <input
          id="discussion-message"
          ref={inputRef}
          type="text"
          value={draft}
          readOnly={!user}
          onChange={(e) => {
            if (user) {
              setDraft(e.target.value);
            }
          }}
          onClick={() => {
            if (!user) {
              // Ouvrir la connexion ou rediriger
              navigate('/auth');
            }
          }}

          onFocus={() => {
            if (user && window.innerWidth < 768) {
              setIsOpen(true);
            }
          }}
          placeholder={user ? 'Écrire un message…' : 'Connecte-toi pour commenter'}
          className={`
          flex-1 min-w-0
          px-4 py-3
          rounded-lg
          border border-background-300
          text-sm text-foreground-900
          placeholder:text-foreground-400
          focus:outline-none
          focus:border-primary-400
          focus:ring-2
          focus:ring-primary-100
          ${!user ? 'bg-background-100 cursor-pointer' : 'bg-background-50'}
        `} />

        <button
          type="submit"
          disabled={!draft.trim()}
          className="
            flex items-center justify-center gap-1.5
            px-4 py-3
            rounded-lg
            bg-primary-500
            text-white
            text-sm font-semibold
            hover:bg-primary-600
            transition-colors
            cursor-pointer
            whitespace-nowrap
            disabled:opacity-50
            disabled:cursor-not-allowed
          "
        >
          <i className="ri-send-plane-fill"></i>

          <span className="hidden md:inline">
            Envoyer
          </span>
        </button>
      </form>

      {mobileActions && (
        <div className="md:hidden">
          {mobileActions}
        </div>
      )}
    </section>
  );
}

